import { type ComponentProps, type ReactNode } from "react";
import Link from "next/link";
import clsx from "clsx";

/**
 * Button — the gallery control (DESIGN.md §2, §6). One quiet, confident style; no rounded/shadowed
 * eCommerce buttons. Renders as a <Link> when `href` is set, else a <button>.
 *
 * Variants:
 *   - "solid"  (default) — near-black fill, bone text; hover → gold fill, black text.
 *   - "outline" — hairline outline on bone; hover → gold border.
 *   - "quiet"  — text-only with the gallery underline (for tertiary actions).
 */
// Variants include explicit ON-DARK versions (…-light) so callers never hand-override colors —
// hand-overrides collide with the variant's own text/border class (Tailwind source order, not
// className order, decides the winner) and can render invisibly. Pick the variant to match the bg.
type Variant = "solid" | "outline" | "quiet" | "solid-light" | "outline-light" | "quiet-light";

const base =
	"label-caps inline-flex items-center justify-center gap-2 transition-colors duration-300 ease-gallery disabled:cursor-not-allowed disabled:opacity-40";

const variants: Record<Variant, string> = {
	// On bone / light grounds:
	solid: "bg-mg-black px-7 py-3.5 text-bone hover:bg-mg-gold hover:text-mg-black",
	outline: "border border-mg-black/30 px-7 py-3.5 text-mg-black hover:border-mg-gold-deep",
	quiet:
		"text-mg-black underline decoration-mg-gold-deep/50 underline-offset-4 hover:decoration-mg-gold-deep",
	// On near-black / dark grounds:
	"solid-light": "bg-bone px-7 py-3.5 text-mg-black hover:bg-mg-gold",
	"outline-light": "border border-bone/40 px-7 py-3.5 text-bone hover:border-mg-gold hover:text-mg-gold",
	"quiet-light": "text-bone underline decoration-mg-gold/50 underline-offset-4 hover:decoration-mg-gold",
};

type CommonProps = { variant?: Variant; className?: string; children: ReactNode };

export function Button({
	variant = "solid",
	className,
	children,
	href,
	...rest
}: CommonProps & { href?: string } & Partial<ComponentProps<"button">>) {
	const cls = clsx(base, variants[variant], className);
	if (href) {
		return (
			<Link href={href} className={cls}>
				{children}
			</Link>
		);
	}
	return (
		<button className={cls} {...rest}>
			{children}
		</button>
	);
}
